import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { RendererProps } from "./types";
import { PdfMarkingWorkspace } from "@/study/pdf-marking-workspace";
import { takeAutostartPayload } from "@/study/generate-from-marks";

// Run-mode adapter for the lecture `lecture.marking` step. The whole marking
// experience lives in the reusable <PdfMarkingWorkspace>; this thin wrapper just
// binds it to the run contract (reads the PDF ref off start_inputs, forwards the
// marking payload to /runs/{id}/advance) and handles the Study-mode hand-off
// (auto-advance with a stashed payload — see study/generate-from-marks.ts).
export function LectureMarkingRenderer({ run, submitting, error, onAdvance }: RendererProps) {
  const { t } = useTranslation();
  const start = run.start_inputs as Record<string, unknown>;
  const pdfRef = String(start.local_pdf_ref ?? "");
  const language = String(start.language ?? "en");

  // Forward-compat: a Study-mode "Generate" stashed the marks payload keyed by
  // run id. Auto-advance once so the learner doesn't re-confirm marks they
  // already made. If the advance later errors, the payload is gone and they can
  // re-mark + Generate manually.
  const autoFired = useRef(false);
  useEffect(() => {
    if (autoFired.current || submitting) return;
    const payload = takeAutostartPayload(run.id);
    if (!payload) return;
    autoFired.current = true;
    onAdvance(payload as unknown as Record<string, unknown>);
  }, [run.id, submitting, onAdvance]);

  return (
    <PdfMarkingWorkspace
      pdfRef={pdfRef}
      documentRef={run.id}
      language={language}
      error={error}
      footerNote={t("Marks auto-save on this device — resume anytime from Create.")}
      primaryAction={{
        label: t("Generate study set"),
        loading: submitting,
        onClick: (payload) => onAdvance(payload as unknown as Record<string, unknown>),
      }}
    />
  );
}
